/**
 * @fileoverview Helpers for the Sales Dashboard sections.
 * @description Pure functions the dashboard components share: a month label from the backend's
 * `YYYY-MM`, a due date label from `YYYY-MM-DD`, the My Page status split derived from the
 * section's counts (the backend sends the overlapping `achieved` and `awaitingPayment`, not one
 * count per status), and the bar height of a month. Nothing here knows the caller or the role.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type {
  DashboardMonth,
  DashboardMyPage,
  SalesDashboard,
} from '@/features/sales/types/sales-dashboard.types'

/** @description The shortest bar a month draws, so a 0% or empty month is still visible. */
const MIN_BAR_PERCENT = 8

/**
 * @description The locale a date is written in: the language without its region (a browser may
 * report `en-US`), with English kept day first, as the list has always read.
 * @param {string} language - The UI language.
 * @returns {string} The locale to format with.
 */
function languageOf(language: string): string {
  const base = language.split('-')[0] ?? 'en'
  return base === 'en' ? 'en-GB' : base
}

/**
 * @description A `YYYY-MM-DD` date as it reads on the Needs Attention list, in the language the
 * screen is shown in.
 * @param {string} isoDate - The date.
 * @param {string} [language] - The UI language (e.g. `hi`); English (day first) when omitted.
 * @returns {string} For example `30 Nov 2026`, or the input when it is not a date.
 */
export function dueDateLabel(isoDate: string, language = 'en'): string {
  const time = Date.parse(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(time)) return isoDate
  return new Date(time).toLocaleDateString(languageOf(language), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** @description The My Page targets split into the segments of the card's bar. */
export interface MyPageSplit {
  awaitingPayment: number
  atRisk: number
  overdue: number
  /** Everything the API does not count separately (on track, on hold, completed or closed), so the
   * bar adds up to the total. */
  other: number
}

/**
 * @description Splits the My Page section into the bar's segments. The API sends the total and the
 * three disjoint status counts (awaiting payment, at risk, overdue) but no count for the rest, and
 * `achieved` overlaps them, so the remainder is shown as one honest "Other" segment instead of
 * guessing Completed or On Track from it.
 * @param {DashboardMyPage} myPage - The My Page section.
 * @returns {MyPageSplit} The segments, none negative.
 */
export function myPageSplit(myPage: DashboardMyPage): MyPageSplit {
  const other = Math.max(myPage.total - myPage.awaitingPayment - myPage.atRisk - myPage.overdue, 0)
  return {
    awaitingPayment: myPage.awaitingPayment,
    atRisk: myPage.atRisk,
    overdue: myPage.overdue,
    other,
  }
}

/**
 * @description The height of a month's bar, as a percentage of the chart: its achievement, never
 * below a stub so an empty month still shows.
 * @param {DashboardMonth} month - The month.
 * @returns {number} The height, 8 to 100.
 */
export function monthBarHeight(month: DashboardMonth): number {
  return Math.max(month.percent ?? 0, MIN_BAR_PERCENT)
}

/**
 * @description Whether the caller has no sales records at all in any section they can see: no
 * clients, no backlog, no assigned targets, no My Page targets and nothing needing attention. Only
 * sections the backend sent count; a section left out is not a zero.
 * @param {SalesDashboard} dashboard - The dashboard.
 * @returns {boolean} True when every figure of every present record section is zero.
 */
export function hasNoSalesRecords(dashboard: SalesDashboard): boolean {
  const { activeClients, backlog, achievement, myPage, needsAttention } = dashboard
  const figures = [
    activeClients?.total,
    backlog?.total,
    achievement?.assigned,
    myPage?.total,
    needsAttention?.count,
  ].filter((figure): figure is number => figure !== undefined)
  return figures.length > 0 && figures.every((figure) => figure === 0)
}
